import { ColumnId, CompiledView, RowId, SheetSource } from "./sheetTypes";

/**
 * Search over the CURRENT view. Two consequences follow from that and both are
 * deliberate: the counter never promises matches a person cannot scroll to, and
 * a filtered-out row is not a hit. When the view hides everything that matched,
 * countMatchesOutsideView says so instead of leaving a bare "0 / 0".
 *
 * A match is a CELL that contains the query, which is the same unit the viewer
 * has always counted.
 */

export interface SheetMatch {
  rowId: RowId;
  columnId: ColumnId;
  /** Position in the current view - what the grid scrolls to. */
  rowPos: number;
  colPos: number;
}

export interface MatchSet {
  matches: SheetMatch[];
  truncated: boolean;
}

/**
 * What "the current match" is pinned to. Storing identity rather than an index
 * is what keeps the highlight on the same cell when the sort order changes; an
 * index into a freshly built array points at a different row every time.
 */
export interface MatchAnchor {
  rowId: RowId;
  columnId: ColumnId;
  rowPos: number;
}

export const MAX_MATCHES = 5000;

export function toAnchor(match: SheetMatch): MatchAnchor {
  return { rowId: match.rowId, columnId: match.columnId, rowPos: match.rowPos };
}

export function findMatches(
  view: CompiledView,
  query: string,
  limit: number = MAX_MATCHES,
): MatchSet {
  const needle = query.toLowerCase();
  if (!needle) return { matches: [], truncated: false };

  const matches: SheetMatch[] = [];
  const rows = view.rows;
  const columns = view.columns;

  for (let r = 0; r < rows.length; r++) {
    const cells = rows[r].cells;
    for (let c = 0; c < columns.length; c++) {
      const raw = cells[columns[c].sourceIndex];
      if (raw == null || raw === "") continue;
      if (raw.toLowerCase().indexOf(needle) === -1) continue;
      matches.push({
        rowId: rows[r].sourceIndex,
        columnId: columns[c].id,
        rowPos: r,
        colPos: c,
      });
      if (matches.length >= limit) {
        return { matches, truncated: true };
      }
    }
  }
  return { matches, truncated: false };
}

/**
 * Where the highlight lands after the view changed under it: the same cell if
 * it is still on screen, otherwise the nearest match below where it used to be.
 */
export function resolveActiveIndex(
  matches: SheetMatch[],
  anchor: MatchAnchor | null,
): number {
  if (matches.length === 0) return -1;
  if (!anchor) return 0;

  for (let i = 0; i < matches.length; i++) {
    if (
      matches[i].rowId === anchor.rowId &&
      matches[i].columnId === anchor.columnId
    ) {
      return i;
    }
  }
  for (let i = 0; i < matches.length; i++) {
    if (matches[i].rowPos >= anchor.rowPos) return i;
  }
  return matches.length - 1;
}

export interface OutsideMatches {
  /** Rows that match but are filtered out or hidden. */
  rows: number;
  /** Rows whose only match sits in a hidden column. */
  hiddenColumnRows: number;
}

/**
 * Only called when the view itself has no matches: it turns a dead end
 * ("0 / 0") into an explanation ("12 rows match, but your filters hide them").
 */
export function countMatchesOutsideView(
  source: SheetSource,
  view: CompiledView,
  query: string,
): OutsideMatches {
  const needle = query.toLowerCase();
  if (!needle) return { rows: 0, hiddenColumnRows: 0 };

  const visibleRowIds = new Set<number>();
  for (let i = 0; i < view.rows.length; i++) {
    visibleRowIds.add(view.rows[i].sourceIndex);
  }
  const visibleColumns = new Set<string>();
  for (let i = 0; i < view.columns.length; i++) {
    visibleColumns.add(view.columns[i].id);
  }

  let rows = 0;
  let hiddenColumnRows = 0;

  for (let r = 0; r < source.rows.length; r++) {
    const row = source.rows[r];
    const onScreen = visibleRowIds.has(row.sourceIndex);
    let matchedAnywhere = false;
    let matchedHiddenColumnOnly = true;

    for (let c = 0; c < source.columns.length; c++) {
      const raw = row.cells[source.columns[c].sourceIndex];
      if (raw == null || raw === "") continue;
      if (raw.toLowerCase().indexOf(needle) === -1) continue;
      matchedAnywhere = true;
      if (visibleColumns.has(source.columns[c].id)) {
        matchedHiddenColumnOnly = false;
        break;
      }
    }

    if (!matchedAnywhere) continue;
    if (!onScreen) {
      rows++;
    } else if (matchedHiddenColumnOnly) {
      hiddenColumnRows++;
    }
  }

  return { rows, hiddenColumnRows };
}
